/**
 * Measure the pixel position of a caret offset within an `<input>` so the
 * mention picker can be anchored at the caret (matching the Lexical body
 * editor). Uses the well-known hidden-mirror technique: clone the input's text
 * metrics into an off-screen element and measure where a marker lands.
 *
 * In jsdom (tests) layout is not computed, so offsets resolve to 0 — callers
 * should treat the result as best-effort positioning, not a correctness signal.
 */

export interface CaretCoordinates {
  top: number;
  left: number;
  height: number;
}

const MIRRORED_PROPERTIES = [
  'boxSizing',
  'width',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
  'borderTopWidth',
  'borderRightWidth',
  'borderBottomWidth',
  'borderLeftWidth',
  'fontStyle',
  'fontVariant',
  'fontWeight',
  'fontStretch',
  'fontSize',
  'lineHeight',
  'fontFamily',
  'textAlign',
  'textTransform',
  'textIndent',
  'letterSpacing',
  'wordSpacing',
] as const;

export function getCaretCoordinates(
  input: HTMLInputElement,
  position: number
): CaretCoordinates {
  const doc = input.ownerDocument;
  const win = doc.defaultView ?? window;
  const computed = win.getComputedStyle(input);

  const mirror = doc.createElement('div');
  const style = mirror.style;
  style.position = 'absolute';
  style.visibility = 'hidden';
  style.whiteSpace = 'pre';
  style.overflow = 'hidden';
  style.width = 'auto';

  MIRRORED_PROPERTIES.forEach((prop) => {
    style[prop] = computed[prop];
  });

  // `white-space: pre` preserves spaces, so the substring measures faithfully.
  mirror.textContent = input.value.slice(0, position);

  const marker = doc.createElement('span');
  // A non-empty marker guarantees a measurable box at the caret.
  marker.textContent = input.value.slice(position) || '.';
  mirror.appendChild(marker);

  doc.body.appendChild(mirror);
  const top = marker.offsetTop + parseInt(computed.borderTopWidth, 10);
  const left = marker.offsetLeft + parseInt(computed.borderLeftWidth, 10);
  const height = parseInt(computed.lineHeight, 10) || input.offsetHeight;
  doc.body.removeChild(mirror);

  return { top, left, height };
}
